import React, { useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import { adminLogin, listLeads } from "@/lib/api";
import { Lock, LogOut, RefreshCw, Mail, Phone, Search } from "lucide-react";

const STORAGE_KEY = "zp_admin_token";

export default function AdminLeads() {
  const [pw, setPw] = useState("");
  const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  const load = async (t) => {
    setLoading(true);
    try {
      const data = await listLeads(t);
      setLeads(data);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load leads. Please re-login.");
      localStorage.removeItem(STORAGE_KEY);
      setToken("");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load(token);
  }, [token]);

  const onLogin = async (e) => {
    e.preventDefault();
    try {
      const { token: t } = await adminLogin(pw);
      localStorage.setItem(STORAGE_KEY, t);
      setToken(t);
      toast.success("Welcome.");
    } catch {
      toast.error("Invalid password.");
    }
  };

  const onLogout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setToken("");
    setLeads([]);
  };

  const filtered = leads.filter((l) => {
    const s = q.toLowerCase().trim();
    if (!s) return true;
    return [l.name, l.company, l.email, l.phone, l.origin_country, l.destination, l.cargo_type, l.source]
      .filter(Boolean)
      .some((v) => String(v).toLowerCase().includes(s));
  });

  if (!token) {
    return (
      <div className="min-h-screen grid place-items-center bg-[color:var(--zp-bg)] px-6">
        <Toaster theme="light" richColors position="top-right" />
        <form
          data-testid="admin-login-form"
          onSubmit={onLogin}
          className="zp-card p-8 w-full max-w-sm"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-[color:var(--zp-bg-2)] grid place-items-center text-[color:var(--zp-cyan-3)]">
              <Lock size={18} />
            </div>
            <div>
              <div className="zp-label">Zopran Admin</div>
              <h1 className="font-display text-xl">Leads Console</h1>
            </div>
          </div>
          <label className="block">
            <span className="zp-label mb-2 block">Admin Password</span>
            <input
              data-testid="admin-password-input"
              type="password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              className="zp-input"
              placeholder="••••••••"
              autoFocus
            />
          </label>
          <button data-testid="admin-login-btn" className="zp-btn-primary w-full mt-6">
            Enter
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[color:var(--zp-bg)] text-[color:var(--zp-navy)]">
      <Toaster theme="light" richColors position="top-right" />
      <header className="border-b border-[color:var(--zp-border)] bg-white/85 backdrop-blur-xl sticky top-0 z-30 shadow-[0_4px_24px_-12px_rgba(10,37,64,0.12)]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="zp-label">Zopran</div>
            <span className="text-[color:var(--zp-text-3)]">·</span>
            <span className="font-display tracking-tight">Leads Console</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="admin-refresh-btn"
              onClick={() => load(token)}
              className="zp-btn-ghost text-sm"
              disabled={loading}
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button data-testid="admin-logout-btn" onClick={onLogout} className="zp-btn-ghost text-sm">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-10">
        <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
          <div>
            <div className="zp-label">Inbox</div>
            <h2 className="font-display text-3xl tracking-tight mt-1">
              {filtered.length} lead{filtered.length === 1 ? "" : "s"}
            </h2>
          </div>
          <label className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--zp-text-3)]" />
            <input
              data-testid="admin-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, company, email…"
              className="zp-input pl-9 w-80"
            />
          </label>
        </div>

        <div className="zp-card overflow-hidden p-0">
          <div className="grid grid-cols-12 px-5 py-3 text-xs uppercase tracking-[0.18em] text-[color:var(--zp-text-3)] border-b border-[color:var(--zp-border)] bg-[color:var(--zp-bg)]">
            <div className="col-span-3">Name / Company</div>
            <div className="col-span-3">Contact</div>
            <div className="col-span-3">Route / Cargo</div>
            <div className="col-span-2">Source</div>
            <div className="col-span-1 text-right">Time</div>
          </div>
          {filtered.length === 0 && (
            <div className="px-5 py-10 text-center text-[color:var(--zp-text-3)] text-sm">
              {loading ? "Loading leads…" : "No leads yet."}
            </div>
          )}
          {filtered.map((l) => (
            <div
              key={l.id}
              data-testid={`admin-lead-row-${l.id}`}
              className="grid grid-cols-12 px-5 py-4 text-sm border-b border-[color:var(--zp-border)] last:border-b-0 hover:bg-[color:var(--zp-bg)]"
            >
              <div className="col-span-3">
                <div className="font-display tracking-tight">{l.name}</div>
                <div className="text-xs text-[color:var(--zp-text-3)] mt-0.5">{l.company || "—"}</div>
              </div>
              <div className="col-span-3 space-y-1">
                <a href={`mailto:${l.email}`} className="flex items-center gap-2 text-[color:var(--zp-navy)] hover:text-[color:var(--zp-cyan-3)]">
                  <Mail size={12} className="text-[color:var(--zp-cyan-3)]" /> {l.email}
                </a>
                {l.phone && (
                  <a href={`tel:${l.phone}`} className="flex items-center gap-2 text-[color:var(--zp-text-2)] hover:text-[color:var(--zp-cyan-3)]">
                    <Phone size={12} className="text-[color:var(--zp-cyan-3)]" /> {l.phone}
                  </a>
                )}
              </div>
              <div className="col-span-3 text-[color:var(--zp-navy)]">
                <div>{l.origin_country || "—"} → {l.destination || "—"}</div>
                <div className="text-xs text-[color:var(--zp-text-3)] mt-0.5">{l.cargo_type || "—"}</div>
              </div>
              <div className="col-span-2">
                <span className="text-[11px] px-2.5 py-1 rounded-full border border-[color:var(--zp-border)] bg-[color:var(--zp-bg)] text-[color:var(--zp-text-2)]">
                  {l.source || "website"}
                </span>
              </div>
              <div className="col-span-1 text-right text-xs text-[color:var(--zp-text-3)]">
                {new Date(l.created_at).toLocaleString()}
              </div>
              {l.shipment_details && (
                <div className="col-span-12 mt-3 text-xs text-[color:var(--zp-text-2)] bg-[color:var(--zp-bg)] rounded-lg p-3">
                  {l.shipment_details}
                </div>
              )}
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
